// User types
export interface User {
  id: number;
  name: string;
  email: string;
  email_verified_at: string | null;
  avatar_url: string | null;
  onboarding_completed: boolean;
  builder_tour_completed: boolean;
  preferences: Record<string, unknown>;
  marketing_emails: boolean;
  roles: string[];
  permissions: string[];
  created_at: string;
  current_team_id: number | null;
  current_team: Team | null;
  teams: Team[];
}

// Auth types
export interface AuthResponse {
  user: User;
  token: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterCredentials {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  invite_token?: string;
  captcha_token?: string;
  etsy_theme?: string;
}

// API Response types
export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    current_page: number;
    total_pages: number;
    total: number;
    per_page: number;
  };
}

// Billing types
export interface BillingProduct {
  id: string;
  name: string;
  description: string;
  price: number;
  promo_price?: number;
  stripe_price: string | null;
  type: 'one_time';
  features: string[];
}

export interface BillingSubscription {
  id: string;
  name: string;
  description: string;
  price_yearly: number;
  stripe_price_yearly: string | null;
  type: 'subscription';
  features: string[];
}

export interface BillingPlansResponse {
  products: Record<string, BillingProduct>;
  subscriptions: Record<string, BillingSubscription>;
  promo_active: boolean;
}

export interface PaidInvite {
  id: number;
  title: string;
  paid_at: string;
  payment_type: string;
  status: string;
}

export interface BillingInvite {
  id: number;
  title: string;
  paid_at: string | null;
  refunded_at: string | null;
  payment_type: string | null;
  status: string;
  created_at: string;
  has_submitted_rsvps: boolean;
  has_emails_sent: boolean;
}

export interface BillingUsage {
  plan: BillingSubscription | null;
  usage: {
    published_invites: number;
    paid_invites: PaidInvite[];
    all_invites: BillingInvite[];
  };
  subscribed: boolean;
  table_plan_enabled: boolean;
}

export interface PublishGateError {
  message: string;
  error_code: 'payment_required' | 'invite_limit_reached';
  price?: number;
  full_price?: number;
  has_promo?: boolean;
}

export interface Subscription {
  subscribed: boolean;
  on_trial: boolean;
  on_grace_period: boolean;
  plan: string | null;
  status: string | null;
  ends_at: string | null;
  trial_ends_at: string | null;
  current_period_end: string | null;
}

export interface CheckoutResponse {
  checkout_url?: string;
  redirect_url?: string;
  message?: string;
}

export interface PortalResponse {
  portal_url: string;
}

// Notification types
export interface NotificationData {
  title: string;
  message: string;
  action_url?: string;
  action_label?: string;
  icon?: string;
  color?: 'success' | 'warning' | 'error' | 'info';
}

export interface Notification {
  id: string;
  type: string;
  data: NotificationData;
  read_at: string | null;
  created_at: string;
}

export interface NotificationsResponse {
  notifications: Notification[];
  unread_count: number;
}

// Admin types
export interface AdminStats {
  total_users: number;
  active_subscriptions: number;
  signups_this_week: number;
  signups_this_month: number;
  mrr: number;
  invites_sold: number;
  total_revenue: number; // in pence (per_invite + bespoke only)
  table_plans_sold: number;
  table_plan_revenue: number; // in pence
  total_invites: number;
  published_invites: number;
  avg_days_to_sale?: number | null;
  conversion_rate?: number;
}

export interface AdminSale {
  id: number;
  title: string;
  user_name: string | null;
  user_email: string | null;
  team_name: string | null;
  amount_paid: number | null; // in pence
  payment_type: string;
  paid_at: string;
  refunded_at: string | null;
}

export interface AdminUserSubscription {
  status: string;
  plan: string | null;
  plan_name: string;
  ends_at: string | null;
  trial_ends_at: string | null;
  created_at: string;
  on_grace_period: boolean;
}

export interface AdminUserTeam {
  id: number;
  name: string;
  role: string;
}

export interface AdminUserInvite {
  id: number;
  title: string;
  status: string;
  paid_at: string | null;
  refunded_at: string | null;
  amount_paid: number | null;
  payment_type: string | null;
  is_manual: boolean;
  created_at: string;
  is_shared: boolean;
}

export interface AdminPublishAttempt {
  id: number;
  title: string;
  status: string;
  publish_attempted_at: string;
}

export interface AdminUser {
  id: number;
  name: string;
  email: string;
  avatar_url: string | null;
  email_verified_at: string | null;
  last_login_at: string | null;
  has_google_login: boolean;
  roles: string[];
  stripe_id: string | null;
  subscription: AdminUserSubscription | null;
  teams: AdminUserTeam[];
  created_at: string;
  utm_source: string | null;
  suspended_at: string | null;
  banned_at: string | null;
  marketing_unsubscribed_at: string | null;
  last_seen_ip: string | null;
  fingerprint: string | null;
  country_code: string | null;
  fingerprint_flagged: boolean;
  publish_attempts?: AdminPublishAttempt[];
}

export interface AdminUsersResponse {
  users: AdminUser[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface AdminTeamUsage {
  id: number;
  name: string;
  owner_name: string | null;
  members_count: number;
  plan: string;
  invites_total: number;
  invites_published: number;
  invites_drafts: number;
  guests: number;
  emails_sent: number;
  rsvp_yes: number;
  rsvp_no: number;
  rsvp_maybe: number;
  rsvp_pending: number;
  views: number;
  images: number;
  active_locales: string[];
  created_at: string;
}

export interface AdminTeamUsageTotals {
  teams: number;
  invites: number;
  published: number;
  guests: number;
  emails_sent: number;
  rsvp_yes: number;
  rsvp_no: number;
  views: number;
  images: number;
  top_theme: string | null;
  ai_sketch_gens?: number;
  guests_with_email?: number;
  marketing_subscribed?: number;
}

export interface AdminTeamUsageResponse {
  teams: AdminTeamUsage[];
  totals: AdminTeamUsageTotals;
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface AdminUserUpdateData {
  name?: string;
  email?: string;
  disabled?: boolean;
  role?: string;
}

export interface UserEmailLog {
  email_type: string;
  subject: string;
  sent_at: string;
  open_count: number;
  last_opened_at: string | null;
}

export interface AdminEmailLog {
  id: number;
  user_id: number;
  user_name: string | null;
  user_email: string | null;
  email_type: string;
  subject: string;
  sent_at: string;
  open_count: number;
  last_opened_at: string | null;
}

export interface EmailLogStats {
  total_sent: number;
  total_opened: number;
  open_rate: number;
}

export interface AdminEmailLogsResponse {
  stats: EmailLogStats;
  logs: AdminEmailLog[];
  meta: {
    current_page: number;
    last_page: number;
    total: number;
  };
}

export interface EmailBatch {
  batch_id: string;
  email_type: string;
  subject: string;
  sent_at: string;
  total_sent: number;
  total_opened: number;
  open_rate: number;
}

export interface EmailBatchesResponse {
  data: EmailBatch[];
}

export interface FollowUpRecipient {
  id: number;
  name: string;
  email: string;
  signed_up: string;
  draft_edited: string | null;
}

export interface FollowUpDryRunResult {
  dry_run: true;
  would_send: number;
  skipped: number;
  recipients: FollowUpRecipient[];
}

export interface FollowUpSendResult {
  dry_run: false;
  sent: number;
  skipped: number;
}

export interface OfferEmailFields {
  subject: string;
  headline: string;
  body: string;
  cta_label: string;
  cta_url: string;
  discount_code: string;
}

export interface OfferRecipient {
  id: number;
  name: string;
  email: string;
  signed_up: string;
}

export interface OfferDryRunResult {
  dry_run: true;
  would_send: number;
  recipients: OfferRecipient[];
}

export interface OfferSendResult {
  dry_run: false;
  sent: number;
}

export type ImageWidth = 'full' | 'large' | 'medium' | 'small';
export type ImageAlign = 'left' | 'center' | 'right';
export type ImagePosition = 'top' | 'above_cta';

export interface AnnouncementEmailFields {
  subject: string;
  headline: string;
  body: string;
  cta_label: string;
  cta_url: string;
  image_url: string;
  image_width: ImageWidth;
  image_align: ImageAlign;
  image_position: ImagePosition;
}

export interface AnnouncementDryRunResult {
  dry_run: true;
  would_send: number;
  recipients: OfferRecipient[];
}

export interface AnnouncementSendResult {
  dry_run: false;
  sent: number;
}

export interface NewsletterFeature {
  title: string;
  description: string;
  image_url?: string;
}

export interface NewsletterBlogPost {
  title: string;
  excerpt: string;
  url: string;
  image?: string;
}

export interface NewsletterEmailFields {
  subject: string;
  intro_text: string;
  features: NewsletterFeature[];
  cta_label: string;
  cta_url: string;
  blog_post: NewsletterBlogPost | null;
}

export interface NewsletterDryRunResult {
  dry_run: true;
  would_send: number;
  recipients: OfferRecipient[];
}

export interface NewsletterSendResult {
  dry_run: false;
  sent: number;
}

export interface EmailDraft {
  id: number;
  name: string;
  email_type: string;
  fields: Record<string, unknown>;
  created_by: number;
  creator?: { id: number; name: string };
  created_at: string;
  updated_at: string;
}

// Team types
export type TeamRole = 'owner' | 'admin' | 'member';
export type TeamBillingType = 'flat' | 'per_seat';

export interface Team {
  id: number;
  name: string;
  slug: string;
  owner_id: number;
  billing_type: TeamBillingType;
  max_seats: number | null;
  seat_count: number;
  created_at: string;
  updated_at: string;
  owner?: TeamOwner;
  members?: TeamMember[];
  pending_invites?: TeamInvite[];
  user_role?: TeamRole;
}

export interface TeamOwner {
  id: number;
  name: string;
  email: string;
  avatar_url: string | null;
}

export interface TeamMember {
  id: number;
  name: string;
  email: string;
  avatar_url: string | null;
  role: TeamRole;
  joined_at: string;
}

export interface TeamInvite {
  id: number;
  email: string;
  role: 'admin' | 'member';
  invited_by: {
    id: number;
    name: string;
  };
  expires_at: string;
  created_at: string;
}

export interface TeamInviteDetails {
  email: string;
  role: 'admin' | 'member';
  team: {
    id: number;
    name: string;
  };
  invited_by: {
    name: string;
  };
  expires_at: string;
}

export interface CreateTeamData {
  name: string;
}

export interface UpdateTeamData {
  name?: string;
}

export interface InviteMemberData {
  email: string;
  role: 'admin' | 'member';
}

export interface UpdateMemberRoleData {
  role: 'admin' | 'member';
}

// Content/Item types for browse grid
export interface ContentTag {
  id: number;
  name: string;
  type: string;
}

export interface ContentItem {
  id: number;
  title: string;
  description: string | null;
  image_url: string | null;
  tags: ContentTag[];
  meta?: Record<string, string | number>;
  created_at: string;
}

// Filter types
export interface FilterOption {
  id: string;
  label: string;
  dotColor?: string;
}

export interface FilterCategory {
  id: string;
  label: string;
  options: FilterOption[];
}

export interface SelectedFilter {
  categoryId: string;
  categoryLabel: string;
  optionId: string;
  optionLabel: string;
}

// Guest Tag types
export interface GuestTag {
  id: number;
  name: string;
  color: string | null;
  is_default: boolean;
}

// Invite Builder types
export type InviteStatus = 'draft' | 'published' | 'archived';
export type InviteVisibility = 'public' | 'private';
export type InviteType = 'invite' | 'save-the-date';
export type EventType = 'wedding' | 'anniversary' | 'birthday';
export type RsvpStatus = 'pending' | 'yes' | 'no' | 'maybe';

export interface InviteHeroContent {
  title: string;
  subtitle: string;
  preamble?: string;
  dateDisplay: string;
  dateFormat?: string;
  dateUppercase?: boolean;
  backgroundImageId: number | null;
  showCountdown?: boolean;
  sketchIllustration?: 'dance' | 'manor' | 'arch' | 'rings' | 'table-plan' | 'cake' | 'flowers' | 'custom';
  sketchHeroLayout?: 'split' | 'portrait';
  sketchImageTransform?: { x: number; y: number; scale: number };
  polaroidImageTransform?: { x: number; y: number; scale: number };
  heroYears?: number;
}

export interface InviteLocationContent {
  name: string;
  address: string;
  showMap: boolean;
  coordinates: { lat: number; lng: number } | null;
}

export interface InviteLocationItem {
  id: string;
  label: string;
  name: string;
  address: string;
  showMap: boolean;
  coordinates: { lat: number; lng: number } | null;
  sameAsFirst?: boolean;
  showForTags?: string[];
  arrivalTime?: string;
}

export interface InviteDetailsContent {
  title?: string;
  show_title?: boolean;
  labelText?: string;
  show_label?: boolean;
  location?: InviteLocationContent;
  locations?: InviteLocationItem[];
  dressCode: string;
  additionalInfo: string;
  contactEmail?: string;
  contactPhone?: string;
  sketchShowIllustration?: boolean;
}

export interface InviteScheduleItem {
  id: string;
  time: string;
  description: string;
  showForTags?: string[];
}

export interface InviteScheduleContent {
  title?: string;
  show_title?: boolean;
  labelText?: string;
  show_label?: boolean;
  items: InviteScheduleItem[];
  sketchShowIllustration?: boolean;
}

export interface InviteGalleryContent {
  title?: string;
  show_title?: boolean;
  labelText?: string;
  show_label?: boolean;
  imageIds: number[];
  sketchShowIllustration?: boolean;
}

// ─── Our Story ────────────────────────────────────────────────────────────────

export type StoryGalleryLayout = 'stack' | 'carousel';

export interface OurStoryBlock {
  id: string;
  heading?: string;
  body?: string;
  imagePosition: 'top' | 'bottom';
  imageIds: number[];
  galleryLayout: StoryGalleryLayout;
  autoPlay?: boolean;
}

export interface InviteOurStoryContent {
  title?: string;
  show_title?: boolean;
  labelText?: string;
  show_label?: boolean;
  blocks: OurStoryBlock[];
}

// ─── Table Plan ──────────────────────────────────────────────────────────────

export type TableShape = 'round' | 'rectangular' | 'square';

export interface TablePlanTable {
  id: string;
  name: string;
  shape: TableShape;
  capacity: number;
  x: number;
  y: number;
  color?: string;
  tag_id?: number | null;
  one_sided?: boolean;
  rotation?: number;   // 0 | 90 | 180 | 270 (degrees CW)
  guest_ids: (number | null)[];
}

export interface TablePlanData {
  tables: TablePlanTable[];
}

export interface TablePlan {
  id: number;
  team_id: number;
  invite_id: number | null;
  name: string;
  plan_data: TablePlanData;
  created_at: string;
  updated_at: string;
}

// ─── Custom Questions ─────────────────────────────────────────────────────────

export type CustomQuestionType = 'text' | 'multiple_choice' | 'single_choice';

export interface CustomQuestion {
  id: string;
  type: CustomQuestionType;
  label: string;
  required: boolean;
  options?: string[]; // multiple_choice / single_choice only
  showForTags?: string[];
}

export interface PlusOneTagRule {
  id: string;
  tags: string[];
  max: number;
}

export interface InviteRsvpContent {
  title?: string;
  show_title?: boolean;
  labelText?: string;
  show_label?: boolean;
  collectEmail: boolean;
  sketchShowIllustration?: boolean;
  collectDietary: boolean;
  allowPlusOnes: boolean;
  maxPlusOnes: number;
  allowMaybe?: boolean;
  customMessage: string;
  acceptLabel?: string;
  declineLabel?: string;
  maybeLabel?: string;
  hideFoodForTags?: string[];
  hideMarketingConsent?: boolean;
  customQuestions?: CustomQuestion[];
  plusOneTagRules?: PlusOneTagRule[];
}

export interface InviteFaqItem {
  id: string;
  question: string;
  answer: string;
  showForTags?: string[];
}

export interface InviteFaqContent {
  title?: string;
  show_title?: boolean;
  labelText?: string;
  show_label?: boolean;
  items: InviteFaqItem[];
  sketchShowIllustration?: boolean;
}

export interface InviteGiftsContent {
  title: string;
  description: string;
  registryUrl?: string;
  registryLabel?: string;
  sketchShowIllustration?: boolean;
}

export interface InviteMusicContent {
  enabled: boolean;
  trackId: string | null;
  volume: number; // 0–1, default 0.5
  autoplay: boolean; // start on first interaction (default true)
}

export type InviteNearbyCategory = 'hotel' | 'pub' | 'restaurant' | 'cafe' | 'shop' | 'other';

export interface InviteNearbyItem {
  id: string;
  name: string;
  category: InviteNearbyCategory;
  address?: string;
  url?: string;
  note?: string;
  showForTags?: string[];
}

export interface InviteNearbyContent {
  title?: string;
  show_title?: boolean;
  labelText?: string;
  show_label?: boolean;
  items: InviteNearbyItem[];
  sketchShowIllustration?: boolean;
}

export interface InviteLabelOverrides {
  tapToOpen?: string;
  dietary?: {
    none?: string;
    vegetarian?: string;
    pescatarian?: string;
    vegan?: string;
    other?: string;
  };
  allergies?: {
    nuts?: string;
    dairy?: string;
    gluten?: string;
    shellfish?: string;
    eggs?: string;
    soy?: string;
  };
}

export interface InviteContent {
  hero: InviteHeroContent;
  details: InviteDetailsContent;
  schedule: InviteScheduleContent;
  gallery: InviteGalleryContent;
  ourStory?: InviteOurStoryContent;
  rsvp: InviteRsvpContent;
  faq: InviteFaqContent;
  gifts: InviteGiftsContent;
  music?: InviteMusicContent;
  nearby?: InviteNearbyContent;
  labelOverrides?: InviteLabelOverrides;
}

/**
 * The subset of InviteContent that can be translated.
 * Structural/functional fields (image IDs, RSVP settings, music, gallery) are excluded.
 * This is the single source of truth for what gets stored in invite_translations.content.
 */
export interface InviteTranslatableContent {
  hero?: Pick<InviteHeroContent, 'title' | 'subtitle' | 'preamble' | 'dateDisplay'>;
  details?: Pick<InviteDetailsContent, 'title' | 'locations' | 'dressCode' | 'additionalInfo'>;
  schedule?: InviteScheduleContent;
  rsvp?: Pick<InviteRsvpContent, 'customMessage' | 'customQuestions'>;
  faq?: InviteFaqContent;
  gifts?: Pick<InviteGiftsContent, 'title' | 'description' | 'registryLabel'>;
  nearby?: InviteNearbyContent;
  gallery?: Pick<InviteGalleryContent, 'title'>;
  ourStory?: Pick<InviteOurStoryContent, 'title'>;
  labelOverrides?: InviteLabelOverrides;
}

export interface InviteTranslation {
  locale: string;
  content: InviteTranslatableContent;
}

export const SUPPORTED_LOCALES = [
  { code: 'en',    label: 'English',      flag: '🇬🇧' },
  { code: 'en-US', label: 'English (US)', flag: '🇺🇸' },
  { code: 'es', label: 'Spanish',    flag: '🇪🇸' },
  { code: 'it', label: 'Italian',    flag: '🇮🇹' },
  { code: 'fr', label: 'French',     flag: '🇫🇷' },
  { code: 'de', label: 'German',     flag: '🇩🇪' },
  { code: 'pt', label: 'Portuguese', flag: '🇵🇹' },
  { code: 'nl', label: 'Dutch',      flag: '🇳🇱' },
  { code: 'pl', label: 'Polish',     flag: '🇵🇱' },
  { code: 'sv', label: 'Swedish',    flag: '🇸🇪' },
  { code: 'no', label: 'Norwegian',  flag: '🇳🇴' },
  { code: 'da', label: 'Danish',     flag: '🇩🇰' },
  { code: 'el', label: 'Greek',      flag: '🇬🇷' },
  { code: 'hi', label: 'Hindi',      flag: '🇮🇳' },
  { code: 'ja', label: 'Japanese',   flag: '🇯🇵' },
  { code: 'zh-CN', label: 'Chinese', flag: '🇨🇳' },
] as const;

export type SupportedLocale = typeof SUPPORTED_LOCALES[number]['code'];

export interface InviteThemeColors {
  background: string;
  accent: string;
  headingText: string;
  bodyText: string;
  nav: string;
  button: string;
  darkBackground?: string; // Luxury theme: alternate section bg (Details, Gallery, Gifts)
}

export interface InviteThemeFonts {
  heading: string;
  body: string;
}

export interface InviteTheme {
  preset: string;
  colors: InviteThemeColors;
  fonts: InviteThemeFonts;
  pattern?: string; // For geometric theme - which pattern to use
  lineColor?: string; // For geometric theme - custom line color for light backgrounds
  paletteId?: string; // Explicitly stored palette selection (avoids color-matching brittleness)
  luxuryHideNav?: boolean; // For luxury theme - hide the floating bottom nav/dot nav
  luxuryIllustration?: 'venue' | 'church' | 'none'; // For luxury theme - blueprint SVG illustration (default: 'venue')
  luxurySectionInvert?: string[]; // For luxury theme - section IDs whose dark/light is flipped from default
  typographicHeroLayout?: 'cropped-monogram' | 'stacked-names' | 'script-names' | 'bordered-classic' | 'vertical-names' | 'dark-monogram';
  typographicMonogramStyle?: 'side-by-side' | 'stacked' | 'circle' | 'double-circle' | 'square' | 'overlapping' | 'ruled' | 'diamond' | 'custom';
  typographicMonogramIntro?: boolean;
  typographicSectionInvert?: string[]; // For typographic theme - section IDs switched to dark background
  introSeal?: boolean; // Show a wax seal intro screen before the invite
  introSealVariant?: 'gold' | 'bronze' | 'cream' | 'rust' | 'pink' | 'sage'; // Legacy — use envelopeStyle instead
  envelopeStyle?: string; // Curated envelope+seal style ID (undefined = auto)
}

export interface InviteSectionsEnabled {
  hero: boolean;
  details: boolean;
  schedule: boolean;
  gallery: boolean;
  ourStory: boolean;
  rsvp: boolean;
  faq: boolean;
  gifts: boolean;
  nearby: boolean;
}

/** Per-section tag allowlist — if set, only guests with a matching tag see the section */
export type SectionTagRules = Partial<Record<keyof InviteSectionsEnabled, string[]>>;

export interface InviteImage {
  id: number;
  section: 'hero' | 'gallery' | 'story' | 'monogram';
  url: string;
  order: number;
  metadata: {
    width?: number;
    height?: number;
    mime?: string;
    size?: number;
    original_name?: string;
  } | null;
}

export interface InviteGuestStats {
  total: number;
  pending: number;
  yes: number;
  no: number;
  maybe: number;
}

export interface Invite {
  id: number;
  team_id: number;
  user_id: number;
  title: string;
  slug: string;
  status: InviteStatus;
  visibility: InviteVisibility;
  is_system?: boolean;
  invite_type: InviteType;
  event_type: EventType;
  content: InviteContent;
  theme: InviteTheme;
  sections_enabled: InviteSectionsEnabled;
  section_tag_rules?: SectionTagRules;
  section_order: string[];
  translations?: InviteTranslation[];
  published_at: string | null;
  event_date: string | null;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    name: string;
    email: string;
    avatar_url: string | null;
  };
  team?: {
    id: number;
    name: string;
    slug: string;
  };
  images?: InviteImage[];
  guest_stats?: InviteGuestStats;
  ai_sketch_gens?: number;
  tags?: string[];
  monogram_svg?: string | null;
}

export interface InviteListItem {
  id: number;
  title: string;
  slug: string;
  status: InviteStatus;
  visibility: InviteVisibility;
  is_system?: boolean;
  is_demo?: boolean;
  published_at: string | null;
  paid_at: string | null;
  event_date: string | null;
  created_at: string;
  updated_at: string;
  hero_image: { id: number; url: string } | null;
  hero_content?: InviteHeroContent | null;
  theme?: InviteTheme | null;
  guest_stats?: InviteGuestStats;
  rsvp_content?: { customQuestions?: CustomQuestion[] } | null;
}

export interface InviteGuest {
  id: number;
  invite_id: number;
  token: string;
  name: string;
  email: string | null;
  rsvp_status: RsvpStatus;
  plus_count: number;
  dietary_notes: string | null;
  dietary_preference: string | null;
  allergies: string[] | null;
  parent_guest_id: number | null;
  parent_guest_name?: string | null;
  rsvp_submitted_at: string | null;
  view_count: number;
  first_viewed_at: string | null;
  last_viewed_at: string | null;
  email_sent_at: string | null;
  marked_as_invited_at: string | null;
  unsubscribed_at: string | null;
  created_at: string;
  updated_at: string;
  public_url: string;
  tags?: GuestTag[];
  custom_answers?: Record<string, string>;
}

export interface InviteThemePreset {
  id: string;
  name: string;
  colors: InviteThemeColors;
  fonts: InviteThemeFonts;
}

export interface CreateInviteData {
  title: string;
  event_date?: string;
  theme?: InviteTheme;
}

export interface UpdateInviteData {
  title?: string;
  invite_type?: InviteType;
  content?: Partial<InviteContent>;
  theme?: Partial<InviteTheme>;
  sections_enabled?: Partial<InviteSectionsEnabled>;
  section_tag_rules?: SectionTagRules;
  section_order?: string[];
  event_date?: string | null;
  monogram_svg?: string | null;
}

export interface AddGuestData {
  name: string;
  email?: string;
  tag_ids?: number[];
  dietary_preference?: string;
  allergies?: string[];
  rsvp_status?: string;
}

export interface BulkImportGuestsData {
  guests: AddGuestData[];
}

export interface PublicInviteData {
  guest: {
    id: number;
    name: string;
    email?: string | null;
    rsvp_status: RsvpStatus;
    plus_count: number;
    dietary_notes: string | null;
    dietary_preference: string | null;
    allergies: string[] | null;
    has_responded: boolean;
    tag_names?: string[];
    plus_ones?: { id: number; name: string | null; email: string | null; dietary_preference: string | null; allergies: string[] | null }[];
  } | null;
  invite: {
    id: number;
    title: string;
    invite_type: InviteType;
    event_date?: string | null;
    content: InviteContent;
    theme: InviteTheme;
    sections_enabled: InviteSectionsEnabled;
    section_tag_rules?: SectionTagRules;
    section_order: string[];
    images: InviteImage[];
    translations?: InviteTranslation[];
    tags?: string[];
    monogram_svg?: string | null;
  };
}

export interface SubmitRsvpData {
  rsvp_status: 'yes' | 'no' | 'maybe';
  plus_ones?: { name?: string; email?: string; dietary_preference?: string; allergies?: string[]; custom_answers?: Record<string, string> }[];
  dietary_preference?: string;
  allergies?: string[];
  email?: string;
  name?: string;
  custom_answers?: Record<string, string>;
  allergy_acknowledged?: boolean;
  wedsy_marketing_consent?: boolean;
  tag_name?: string;
}

// Cross-invite guest types
export interface GuestWithInvite extends InviteGuest {
  invite: { id: number; title: string; slug: string; status: InviteStatus; visibility: InviteVisibility; is_system?: boolean };
}

export interface RsvpSummary {
  totals: {
    total_guests: number;
    total_invited: number;
    attending: number;
    not_attending: number;
    maybe: number;
    pending: number;
    total_plus_ones: number;
  };
  per_invite: {
    invite_id: number;
    invite_title: string;
    invite_status: InviteStatus;
    total: number;
    invited: number;
    yes: number;
    no: number;
    maybe: number;
    pending: number;
    plus_ones: number;
  }[];
  recent_responses: {
    id: number;
    name: string;
    email: string | null;
    rsvp_status: RsvpStatus;
    plus_count: number;
    dietary_notes: string | null;
    rsvp_submitted_at: string;
    invite_id: number;
    invite_title: string;
  }[];
}

export interface ActivityEvent {
  type: string;
  user_id: number | null;
  user_name: string | null;
  user_email: string | null;
  session_id: string | null;
  fingerprint: string | null;
  ip: string | null;
  country_code: string | null;
  device_type: string | null;
  properties: Record<string, unknown>;
  occurred_at: string;
}

export interface ActivityStat {
  label: string;
  today: number;
  yesterday: number;
  last7: number;
  prev7: number;
  thisMonth: number;
  lastMonth: number;
  custom: number;
  customPrev: number;
  delta_day: number | null;
  delta_week: number | null;
  delta_month: number | null;
  delta_custom: number | null;
  unit?: string;
}

export type BlogPostStatus = 'idea' | 'draft' | 'ready' | 'wp_draft' | 'scheduled' | 'published';

export interface BlogPost {
  id: number;
  title: string;
  content: string | null;
  brief: string | null;
  keywords: string | null;
  status: BlogPostStatus;
  wp_post_id: number | null;
  wp_category_id: number | null;
  wp_category_name: string | null;
  wp_author_id: number | null;
  scheduled_at: string | null;
  wp_synced_at: string | null;
  needs_wp_sync: boolean;
  created_by: number;
  creator?: { id: number; name: string };
  created_at: string;
  updated_at: string;
}

// Contact Tickets
export type TicketStatus = 'open' | 'replied' | 'closed';

export interface ContactTicketAttachment {
  id: number;
  contact_ticket_id: number;
  contact_ticket_reply_id: number | null;
  filename: string;
  path: string;
  mime_type: string;
  size: number;
  created_at: string;
  updated_at: string;
}

export interface ContactTicketReply {
  id: number;
  contact_ticket_id: number;
  body: string;
  direction: 'inbound' | 'outbound';
  open_count: number;
  last_opened_at: string | null;
  created_at: string;
  updated_at: string;
  attachments?: ContactTicketAttachment[];
}

export interface ContactTicket {
  id: number;
  ticket_number: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: TicketStatus;
  source: string;
  replies?: ContactTicketReply[];
  attachments?: ContactTicketAttachment[];
  replies_count?: number;
  created_at: string;
  updated_at: string;
}

